import { computed, onBeforeUnmount, ref } from 'vue'
import axios from '@axios'

/**
 * Voice-to-text recording composable (RM-163).
 *
 * Records mic audio with MediaRecorder, uploads the clip to the server-side
 * transcription endpoint, and hands the resulting text to a caller-supplied
 * `onTranscript` callback — the caller decides which field it lands in and
 * the member always reviews before sending. Transcription runs server-side
 * (OpenAI) because iOS Safari has no usable Web Speech API; recording works
 * on iOS Safari 14.3+, Android, and desktop with a feature-detected codec.
 *
 * States: idle | requesting | recording | transcribing | error.
 * `cancel()` discards the take (no upload); `stop()` uploads and transcribes.
 * The mic stream is always released on stop/cancel/unmount — the browser's
 * recording indicator must never linger after the UI says recording ended.
 *
 * @param {Object} [options]
 * @param {string}   [options.endpoint='/agent/transcribe']
 * @param {number}   [options.maxDurationMs=120000] auto-stop cap (120s)
 * @param {(text:string)=>void} [options.onTranscript] receives transcribed text
 * @param {import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>} [options.allowEmbedded]
 *   When true, show the mic even inside an embedded iOS web view (the App
 *   Store wrapper). Used to expose the mic to ADMINS ONLY there, so RM staff
 *   can run the controlled does-the-wrapper-have-mic-permission test on prod
 *   without exposing members (a crash there closes the host app).
 */
export function useVoiceTranscription(options = {}) {
  const {
    endpoint = '/agent/transcribe',
    maxDurationMs = 120000,
    onTranscript = () => {},
    allowEmbedded = ref(false),
  } = options

  const state = ref('idle')
  const error = ref(null)
  const elapsedMs = ref(0)

  // Live mic levels for the recording waveform (RM-163 UI v2): a rolling
  // window of RMS samples, 0..1, newest last. Purely cosmetic — if Web Audio
  // is unavailable the bars just stay flat and recording works regardless.
  const LEVEL_BARS = 20
  const levels = ref(new Array(LEVEL_BARS).fill(0))

  // An UNKNOWN embedded iOS WKWebView (e.g. Instagram's in-app browser)
  // exposes getUserMedia, but if the HOST APP lacks NSMicrophoneUsageDescription,
  // requesting the mic makes iOS TERMINATE the app — so hide the mic where the
  // UA has no "Safari/" token and it isn't the installed PWA
  // (navigator.standalone).
  //
  // The RM App Store wrapper (frozen spoofed "iPhone OS 15_0"/"Version/15.0"
  // Safari UA) was explicitly gated here until 2026-08-01, when the controlled
  // admin test on prod verified the wrapper has mic permission AND full
  // transcription works end-to-end — that fingerprint gate is now removed, so
  // store-app members get the mic like every Safari/PWA member.
  function isEmbeddedIosWebView() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
    if (!/iPhone|iPad|iPod/.test(ua)) return false

    return !/Safari\//.test(ua) && !(typeof window !== 'undefined' && window.navigator.standalone)
  }

  function micForceEnabled() {
    try {
      return localStorage.getItem('rmVoiceMicForce') === '1'
    } catch (e) {
      return false
    }
  }

  const isSupported = computed(() => typeof navigator !== 'undefined'
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    && typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
    && (!isEmbeddedIosWebView() || allowEmbedded.value || micForceEnabled()))

  let recorder = null
  let stream = null
  let chunks = []
  let timerId = null
  let autoStopId = null
  let startedAt = 0
  let discardTake = false
  let audioCtx = null
  let levelTimerId = null

  function startLevelMeter() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx || !stream) return
      audioCtx = new Ctx()
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      audioCtx.createMediaStreamSource(stream).connect(analyser)
      const buf = new Uint8Array(analyser.fftSize)
      levelTimerId = setInterval(() => {
        analyser.getByteTimeDomainData(buf)
        let sum = 0
        for (let i = 0; i < buf.length; i++) {
          const d = (buf[i] - 128) / 128
          sum += d * d
        }
        const rms = Math.sqrt(sum / buf.length)
        const next = levels.value.slice(1)
        next.push(Math.min(1, rms * 3.5))
        levels.value = next
      }, 90)
    } catch (e) {
      // Meter is cosmetic — never let it interfere with recording.
    }
  }

  function stopLevelMeter() {
    if (levelTimerId) {
      clearInterval(levelTimerId)
      levelTimerId = null
    }
    if (audioCtx) {
      try {
        audioCtx.close()
      } catch (e) { /* already closed */ }
      audioCtx = null
    }
    levels.value = new Array(LEVEL_BARS).fill(0)
  }

  function pickMimeType() {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return ''
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg', 'audio/mpeg']

    return candidates.find(t => MediaRecorder.isTypeSupported(t)) || ''
  }

  function releaseStream() {
    stopLevelMeter()
    if (stream) {
      stream.getTracks().forEach(t => t.stop())
      stream = null
    }
    if (timerId) {
      clearInterval(timerId)
      timerId = null
    }
    if (autoStopId) {
      clearTimeout(autoStopId)
      autoStopId = null
    }
  }

  async function handleStop() {
    const recordedType = (recorder && recorder.mimeType) || 'audio/webm'
    releaseStream()

    if (discardTake) {
      // Cancelled: throw the take away, never upload.
      chunks = []
      state.value = 'idle'

      return
    }

    if (!chunks.length) {
      // Nothing captured (immediate stop) — leave the composer untouched.
      state.value = 'idle'

      return
    }

    const blob = new Blob(chunks, { type: recordedType })
    chunks = []
    state.value = 'transcribing'

    try {
      const ext = recordedType.includes('mp4') ? 'mp4'
        : recordedType.includes('ogg') ? 'ogg'
          : recordedType.includes('mpeg') ? 'mp3'
            : 'webm'
      const form = new FormData()
      form.append('audio', blob, `voice-input.${ext}`)

      // The browser sets the multipart boundary; don't set Content-Type.
      const { data } = await axios.post(endpoint, form)
      const text = (data && typeof data.text === 'string' ? data.text : '').trim()
      if (text) {
        onTranscript(text)
        state.value = 'idle'
      } else {
        // No speech detected — surface it, leave any existing text alone.
        state.value = 'error'
        error.value = 'empty_transcript'
      }
    } catch (e) {
      state.value = 'error'
      error.value = e?.response?.data?.error || 'transcription_failed'
    }
  }

  async function start() {
    if (!isSupported.value) return
    if (state.value === 'recording' || state.value === 'transcribing' || state.value === 'requesting') return
    error.value = null
    chunks = []
    discardTake = false

    state.value = 'requesting'
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      state.value = 'error'
      error.value = 'mic_denied'

      return
    }

    const mimeType = pickMimeType()
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    } catch (e) {
      recorder = new MediaRecorder(stream)
    }

    recorder.ondataavailable = e => {
      if (e.data && e.data.size) chunks.push(e.data)
    }
    recorder.onstop = handleStop

    recorder.start()
    startLevelMeter()
    state.value = 'recording'
    startedAt = Date.now()
    elapsedMs.value = 0
    timerId = setInterval(() => { elapsedMs.value = Date.now() - startedAt }, 250)
    autoStopId = setTimeout(() => stop(), maxDurationMs)
  }

  function stop() {
    if (recorder && state.value === 'recording') {
      try {
        recorder.stop()
      } catch (e) {
        releaseStream()
        state.value = 'idle'
      }
    }
  }

  // Discard the current take: stop the recorder and release the mic WITHOUT
  // uploading anything.
  function cancel() {
    if (state.value === 'recording' && recorder) {
      discardTake = true
      try {
        recorder.stop()
      } catch (e) {
        releaseStream()
        state.value = 'idle'
      }
    } else if (state.value === 'requesting' || state.value === 'error') {
      releaseStream()
      state.value = 'idle'
      error.value = null
    }
  }

  function reset() {
    error.value = null
    if (state.value === 'error') state.value = 'idle'
  }

  onBeforeUnmount(() => {
    // Component teardown mid-recording: discard, never upload from a dead view.
    discardTake = true
    stop()
    releaseStream()
  })

  return {
    state,
    error,
    elapsedMs,
    levels,
    isSupported,
    start,
    stop,
    cancel,
    reset,
  }
}
